import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, LogOut, User as UserIcon } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/lib/auth";

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const noindex = <Helmet><meta name="robots" content="noindex,nofollow" /></Helmet>;

  if (user === null) {
    return (
      <div className="min-h-screen flex flex-col">
        {noindex}
        <Header />
        <main className="flex-1 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></main>
        <Footer />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col">
        {noindex}
        <Header />
        <main className="flex-1 flex items-center justify-center px-5 py-12">
          <div className="brutal-card p-8 bg-white text-center max-w-md">
            <p className="font-heading text-xl font-bold">Please log in</p>
            <Link to="/login" className="brutal-btn bg-brand-blue text-white mt-5 inline-block px-6 py-3" data-testid="account-login-link">
              Log in →
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col">
      {noindex}
      <Header />
      <main className="flex-1 max-w-3xl w-full mx-auto px-5 sm:px-8 py-12">
        <div className="brutal-card p-8 bg-white" data-testid="account-card">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-brand-yellow border-[3px] border-[#0A0A0A] rounded-2xl flex items-center justify-center shadow-brutal-sm">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-full h-full rounded-2xl object-cover" />
              ) : (
                <UserIcon strokeWidth={3} className="w-7 h-7" />
              )}
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold leading-tight" data-testid="account-name">{user.name}</h1>
              <p className="text-sm text-muted-foreground" data-testid="account-email">{user.email}</p>
              <p className="text-xs mt-1 font-bold uppercase tracking-widest">
                via {user.auth_provider === "google" ? "Google" : user.auth_provider === "both" ? "Google + Email" : "Email"}
              </p>
            </div>
          </div>

          <div className="my-7 border-t-2 border-dashed border-[#0A0A0A]" />

          <button
            onClick={onLogout}
            className="brutal-btn bg-white text-[#0A0A0A] px-5 py-2.5 text-sm inline-flex items-center gap-2"
            data-testid="account-logout-button"
          >
            <LogOut strokeWidth={3} className="w-4 h-4" /> Log out
          </button>
        </div>
      </main>
      <Footer />
    </div>
  );
}
